import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";

const VIEWS = [["all", "All"], ["independent", "Independent"], ["organization", "Organization"]];

const Bar = ({ label, count, max, testId }) => (
  <div className="py-1.5" data-testid={testId}>
    <div className="flex justify-between text-sm"><span className="text-[#B9BDC7]">{label}</span><span className="text-[#F3F4F6]">{count}</span></div>
    <div className="h-2 mt-1 rounded-full bg-[#222634] overflow-hidden"><div className="h-full bg-brass rounded-full" style={{ width: `${max ? (count / max) * 100 : 0}%` }} /></div>
  </div>
);

const Tile = ({ label, value, sub, testId }) => (
  <div className="cg-card p-5" data-testid={testId}>
    <p className="cg-eyebrow">{label}</p>
    <p className="font-serif text-4xl text-[#F3F4F6] mt-2">{value}</p>
    {sub && <p className="text-xs text-[#6B7280] mt-1">{sub}</p>}
  </div>
);

export const AssessmentDropoff = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [view, setView] = useState("all");
  useEffect(() => {
    api.get("/admin/assessment-dropoff").then((r) => setData(r.data)).catch((e) => setErr(formatApiError(e.response?.data?.detail) || e.message));
  }, []);
  if (err) return <p className="text-terracotta text-sm" data-testid="dropoff-error">{err}</p>;
  if (!data) return <p className="cg-eyebrow animate-pulse">Loading…</p>;
  const d = data[view];
  const max = d.started || 1;
  const maxOpen = Math.max(1, ...d.stopped_at.map((b) => b.count));
  return (
    <div className="space-y-6" data-testid="assessment-dropoff">
      <div>
        <h2 className="font-serif text-3xl text-[#F3F4F6]">Full Assessment Drop-off</h2>
        <p className="text-sm text-[#9CA3AF] mt-1">Anonymous counts only, with test and demo accounts excluded{data.earliest_start ? ` · since ${data.earliest_start.slice(0, 10)}` : ""}.</p>
      </div>
      <div className="flex gap-1 p-1 w-fit rounded-full bg-[#0D0E12] border border-[var(--cg-border)]">
        {VIEWS.map(([k, l]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`dropoff-view-${k}`}
            className={`px-4 py-2 rounded-full text-sm transition-colors ${view === k ? "bg-brass text-obsidian font-medium" : "text-[#9CA3AF] hover:text-[#F3F4F6]"}`}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Tile testId="dropoff-started" label="Started" value={d.started} />
        <Tile testId="dropoff-completed" label="Completed" value={d.completed} sub={d.completion_rate != null ? `${d.completion_rate}% of those who started` : null} />
        <Tile testId="dropoff-in-progress" label="Still in progress" value={d.in_progress} />
        <Tile testId="dropoff-inactive" label="Inactive over 30 days" value={d.inactivity.inactive_30d_plus} sub={`${d.inactivity.inactive_8_30d} inactive 8–30 days`} />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="cg-card p-6" data-testid="dropoff-funnel">
          <p className="cg-eyebrow mb-3">How far people got</p>
          <Bar testId="dropoff-m-started" label="Started" count={d.started} max={max} />
          <Bar testId="dropoff-m-ack" label="Read the instructions" count={d.acknowledged_instructions} max={max} />
          {d.milestones.map((m) => <Bar key={m.key} testId={`dropoff-m-${m.key}`} label={m.label} count={m.count} max={max} />)}
          <Bar testId="dropoff-m-completed" label="Portrait completed" count={d.completed} max={max} />
        </div>
        <div className="cg-card p-6" data-testid="dropoff-stopped">
          <p className="cg-eyebrow mb-3">Where unfinished assessments stopped</p>
          <p className="text-xs text-[#6B7280] mb-2">Statements answered so far</p>
          {d.stopped_at.map((b) => <Bar key={b.range} testId={`dropoff-b-${b.range}`} label={b.range} count={b.count} max={maxOpen} />)}
          <p className="text-xs text-[#6B7280] mt-4">Active in last 7 days: {d.inactivity.active_7d} · 8–30 days: {d.inactivity.inactive_8_30d} · over 30 days: {d.inactivity.inactive_30d_plus}</p>
        </div>
      </div>
      <p className="text-xs text-[#6B7280]">"Answered" includes any 21 answers carried over from the free Glimpse ({data.carried_from_glimpse} assessment{data.carried_from_glimpse === 1 ? "" : "s"} began this way). Last activity is the most recent saved answer.</p>
    </div>
  );
};
